/**
 * Which files may `ss update` delete from ~/.claude after redeploying?
 *
 * The cleanup used to delete EVERY file under agents/hooks/skills/scripts absent
 * from the new manifest. That swept three things SmartStack does not own:
 *   - the skill runtime `installCommands` had just provisioned (`npm install` in
 *     skills/ — node_modules is never in the published package, so never in the
 *     manifest): thousands of "Removed orphan" warnings, then a cold npx download
 *     on the next CLI call;
 *   - Claude Code's own folders (skills/synced/, skills/.trash/);
 *   - skills the user wrote.
 *
 * Ownership rules applied here:
 *   1. obsolete — in the previous manifest, not in the new one: SmartStack put
 *      it there, it is no longer shipped → remove.
 *   2. orphan   — on disk, in neither manifest: remove ONLY inside a top-level
 *      entry the new manifest ships (`skills/efcore`, `hooks/git-guard.js`, …).
 *      Foreign entries (`skills/synced`, a user skill) are never touched.
 *   3. runtime  — `node_modules/**` and `package-lock.json` belong to the
 *      package.json of their directory: kept while that package.json is
 *      shipped, removed with it otherwise (a stale node_modules goes as ONE
 *      directory, not file by file).
 *
 * Pure: paths in, paths out (manifest paths may use either separator). The
 * returned paths are relative to the .claude dir, forward-slashed.
 */

export interface UpdateRemovalInput {
  /** Paths of the manifest written by the previous install (empty if none). */
  oldManifest: readonly string[];
  /** Paths of the manifest the redeploy just wrote (or would write, dry-run). */
  newManifest: readonly string[];
  /** Files currently under agents/hooks/skills/scripts, relative to .claude. */
  onDisk: readonly string[];
}

export interface UpdateRemovalPlan {
  /** Files SmartStack deployed before and no longer ships. */
  obsolete: string[];
  /** Untracked files inside SmartStack-owned entries (stale leftovers). */
  orphans: string[];
  /** Whole node_modules directories whose package.json is no longer shipped. */
  staleRuntimeDirs: string[];
}

const NM = '/node_modules/';

export function normalizeManifestPath(p: string): string {
  return p.replace(/\\/g, '/');
}

/** True for any path inside a node_modules directory. */
export function isNodeModulesPath(p: string): boolean {
  const n = normalizeManifestPath(p);
  return n.startsWith('node_modules/') || n.includes(NM);
}

/**
 * The directory owning a runtime artifact (the one holding its package.json),
 * or null when the path is not a runtime artifact.
 */
function runtimeOwnerDir(p: string): { dir: string; nodeModules: boolean } | null {
  const i = p.indexOf(NM);
  if (i !== -1) return { dir: p.slice(0, i), nodeModules: true };
  const slash = p.lastIndexOf('/');
  const base = p.slice(slash + 1);
  if (base === 'package-lock.json') return { dir: p.slice(0, Math.max(slash, 0)), nodeModules: false };
  return null;
}

/** `skills/efcore/agents/x.md` → `skills/efcore`; `hooks/a.js` → `hooks/a.js`. */
function topEntry(p: string): string {
  return p.split('/').slice(0, 2).join('/');
}

export function planUpdateRemovals(input: UpdateRemovalInput): UpdateRemovalPlan {
  const newSet = new Set(input.newManifest.map(normalizeManifestPath));
  const ownedTops = new Set([...newSet].map(topEntry));
  const shipsPackageJson = (dir: string) => newSet.has(`${dir}/package.json`);

  const obsolete: string[] = [];
  const obsoleteSet = new Set<string>();
  for (const raw of input.oldManifest) {
    const p = normalizeManifestPath(raw);
    if (newSet.has(p) || obsoleteSet.has(p)) continue;
    const owner = runtimeOwnerDir(p);
    if (owner && shipsPackageJson(owner.dir)) continue; // live runtime, provisioned by npm
    obsoleteSet.add(p);
    obsolete.push(p);
  }

  const orphans: string[] = [];
  const staleRuntimeDirs: string[] = [];
  const staleDirSet = new Set<string>();
  for (const raw of input.onDisk) {
    const p = normalizeManifestPath(raw);
    if (newSet.has(p) || obsoleteSet.has(p)) continue;
    if (!ownedTops.has(topEntry(p))) continue; // not ours: Claude Code, the user
    const owner = runtimeOwnerDir(p);
    if (owner) {
      if (shipsPackageJson(owner.dir)) continue;
      if (owner.nodeModules) {
        const dir = `${owner.dir}/node_modules`;
        if (!staleDirSet.has(dir)) {
          staleDirSet.add(dir);
          staleRuntimeDirs.push(dir);
        }
        continue;
      }
    }
    orphans.push(p);
  }

  return { obsolete, orphans, staleRuntimeDirs };
}
